import { useState, useRef, useCallback } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import {
  musicClassifierApi,
  type ParsedVideoItem,
  type MusicClassifierResultRow,
  type ExtraVideoItem,
} from "../../api/musicClassifierApi";
import { trackToolAction } from "../../utils/analytics";

const BUTTON_TOOLTIP_OPTIONS = { position: "top" as const };

type VideoInput = { videoId: string; title?: string; description?: string; duration?: string; music_note?: boolean };

function sanitizeFilenameSegment(s: string): string {
  return s.replace(/[/\\:*?"<>|]/g, "").trim() || "";
}

const MusicClassifierTab = () => {
  const [videos, setVideos] = useState<VideoInput[]>([]);
  const [channelId, setChannelId] = useState("");
  const [runGenAIClassification, setRunGenAIClassification] = useState(true);
  const [loadingParse, setLoadingParse] = useState(false);
  const [loadingClassify, setLoadingClassify] = useState(false);
  const [results, setResults] = useState<MusicClassifierResultRow[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [extraVideos, setExtraVideos] = useState<ExtraVideoItem[]>([]);
  const [channelSummary, setChannelSummary] = useState<{
    fromHtml: number;
    extraFromChannel: number;
  } | null>(null);
  const [loadingFetchChannel, setLoadingFetchChannel] = useState(false);
  const [truncatedRunInfo, setTruncatedRunInfo] = useState<{
    totalResultCount: number;
  } | null>(null);
  const [channelInMusicCms, setChannelInMusicCms] = useState<boolean | null>(null);
  const [channelDetails, setChannelDetails] = useState<{
    title: string;
    subscriberCount: number;
    defaultLanguage?: string;
    madeForKids?: boolean;
    contentOwner?: string;
  } | null>(null);
  const [loadingRunHistory, setLoadingRunHistory] = useState(false);
  const [runs, setRuns] = useState<{ runId: string; dateCreated: string; channelId?: string }[]>([]);
  const [compactView, setCompactView] = useState(false);
  const toast = useRef<Toast>(null);

  const MAX_VIDEOS_PER_RUN = 2000;

  const handleHtmlLoad = useCallback(async (html: string) => {
    setLoadingParse(true);
    try {
      const { videos: parsed, channelId: detectedChannelId } =
        await musicClassifierApi.parseHtml(html);
      const musicNoteCount = parsed.filter((v) => v.music_note === true).length;
      setVideos(
        parsed.map((v: ParsedVideoItem) => ({
          videoId: v.videoId,
          duration: v.duration || undefined,
          music_note: v.music_note,
        }))
      );
      if (detectedChannelId) setChannelId(detectedChannelId);
      setResults([]);
      setRunId(null);
      setExtraVideos([]);
      setChannelSummary(null);
      setTruncatedRunInfo(null);
      setChannelInMusicCms(null);
      setChannelDetails(null);
      const detailParts = [`${parsed.length} videos found`];
      if (musicNoteCount > 0) detailParts.push(`${musicNoteCount} with music note`);
      if (detectedChannelId) detailParts.push(`Channel: ${detectedChannelId}`);
      toast.current?.show({
        severity: "success",
        summary: "HTML parsed",
        detail: detailParts.join(" · "),
      });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Parse failed", detail: String(e) });
    } finally {
      setLoadingParse(false);
    }
  }, []);

  const onDropHtml = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      const reader = new FileReader();
      reader.onload = () => handleHtmlLoad(String(reader.result));
      reader.readAsText(acceptedFiles[0]);
    },
    [handleHtmlLoad]
  );

  const { getRootProps: getHtmlRootProps, getInputProps: getHtmlInputProps } = useDropzone({
    onDrop: onDropHtml,
    accept: { "text/html": [".html", ".htm"], "text/plain": [".html", ".htm"] },
    maxFiles: 1,
  });

  const fetchChannelVideos = async () => {
    if (!channelId.trim() || videos.length === 0) return;
    setLoadingFetchChannel(true);
    try {
      const res = await musicClassifierApi.fetchChannelVideos({
        channelId: channelId.trim(),
        videoIdsFromPage: videos.map((v) => v.videoId),
      });
      setExtraVideos(res.extraVideos);
      setChannelSummary({ fromHtml: res.fromHtml, extraFromChannel: res.extraFromChannel });
      const detailParts = [
        `${res.fromHtml} from page`,
        `${res.extraFromChannel} extra from channel (e.g. private/Shorts)`,
      ];
      if (res.extraFromChannel === 0) detailParts[1] = "0 extra from channel";
      toast.current?.show({
        severity: "success",
        summary: "Channel videos loaded",
        detail: detailParts.join(" · ") + ". Click Classify to run classification.",
      });
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : (e as Error)?.message;
      toast.current?.show({ severity: "error", summary: "Fetch failed", detail: String(msg) });
    } finally {
      setLoadingFetchChannel(false);
    }
  };

  const runClassify = async () => {
    const totalVideos = videos.length + extraVideos.length;
    if (totalVideos === 0) {
      toast.current?.show({ severity: "warn", summary: "Load channel page HTML first" });
      return;
    }
    const videosToClassify = [
      ...videos.map((v) => ({ ...v, fromChannelApi: false as const })),
      ...extraVideos.map((e) => ({
        videoId: e.videoId,
        title: e.title,
        description: e.description,
        duration: e.duration,
        fromChannelApi: true as const,
      })),
    ];
    let payload = videosToClassify;
    if (videosToClassify.length > MAX_VIDEOS_PER_RUN) {
      const ok = window.confirm(
        `Runs are limited to ${MAX_VIDEOS_PER_RUN} videos. You have ${videosToClassify.length} videos. Only the first ${MAX_VIDEOS_PER_RUN} will be classified and saved. Continue?`
      );
      if (!ok) return;
      payload = videosToClassify.slice(0, MAX_VIDEOS_PER_RUN);
    }
    trackToolAction("Classifiers", "run_classify");
    setLoadingClassify(true);
    setResults([]);
    setRunId(null);
    setChannelSummary(null);
    setTruncatedRunInfo(null);
    setChannelInMusicCms(null);
    setChannelDetails(null);
    try {
      const {
        runId: id,
        results: res,
        channelSummary: summary,
        truncatedResults,
        totalResultCount,
        channelInMusicCms: cmsFlag,
        channelTitle: chTitle,
        channelSubscriberCount: chSubs,
        channelDefaultLanguage: chLang,
        channelMadeForKids: chMfk,
        channelContentOwner: chOwner,
      } = await musicClassifierApi.classify({
        videos: payload,
        channelId: channelId.trim() || undefined,
      });
      setRunId(id);
      setResults(res);
      setChannelSummary(summary ?? null);
      setChannelInMusicCms(cmsFlag ?? null);
      if (chTitle != null && chSubs != null) {
        setChannelDetails({
          title: chTitle,
          subscriberCount: chSubs,
          ...(chLang != null && { defaultLanguage: chLang }),
          ...(chMfk !== undefined && { madeForKids: chMfk }),
          ...(chOwner != null && { contentOwner: chOwner }),
        });
      }
      if (truncatedResults && totalResultCount != null) {
        setTruncatedRunInfo({ totalResultCount });
      } else if (payload.length < videosToClassify.length) {
        setTruncatedRunInfo({ totalResultCount: videosToClassify.length });
      } else {
        setTruncatedRunInfo(null);
      }
      const detailParts = [`Run ID: ${id}`];
      if (summary && summary.extraFromChannel > 0) {
        detailParts.push(
          `${summary.fromHtml} from page, ${summary.extraFromChannel} extra from channel (e.g. private/Shorts)`
        );
      } else if (summary) {
        detailParts.push(`${summary.fromHtml} from page, 0 extra from channel`);
      }
      if (cmsFlag !== undefined && cmsFlag !== null) {
        detailParts.push(`Channel in Music CMS: ${cmsFlag ? "Yes" : "No"}`);
      }
      toast.current?.show({
        severity: "success",
        summary: "Classification complete",
        detail: detailParts.join(" · "),
      });
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : (e as Error)?.message;
      toast.current?.show({ severity: "error", summary: "Error", detail: String(msg) });
    } finally {
      setLoadingClassify(false);
    }
  };

  const loadRunHistory = async () => {
    setLoadingRunHistory(true);
    try {
      const { runs: list } = await musicClassifierApi.listRuns({ limit: 20 });
      setRuns((list as { runId: string; dateCreated: string; channelId?: string }[]) || []);
    } catch (e: unknown) {
      setRuns([]);
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : (e as Error)?.message;
      toast.current?.show({
        severity: "error",
        summary: "Run history failed",
        detail: String(msg),
      });
    } finally {
      setLoadingRunHistory(false);
    }
  };

  const exportParsedList = useCallback(() => {
    if (videos.length === 0) return;
    const rows = videos.map((v) => ({
      videoId: v.videoId,
      title: v.title ?? "",
      description: v.description ?? "",
      duration: v.duration ?? "",
      music_note: v.music_note === true ? "Yes" : v.music_note === false ? "No" : "",
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv; charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
    const channelPart = channelId.trim() ? sanitizeFilenameSegment(channelId.trim()) : "export";
    link.setAttribute("download", `music-classifier-parsed-${channelPart}-${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [videos, channelId]);

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <h3 className="mt-0">Music Classifier</h3>
      <p className="text-color-secondary mb-4">
        Upload a saved YouTube channel page as <strong>HTML only</strong>. Then run classification and download results.
      </p>

      <div className="flex flex-column gap-3 mb-4">
        <div>
          <label className="block mb-2">Channel page input</label>
          <div className="surface-100 border-round p-3 mb-2 text-sm text-color-secondary">
            <strong className="block mb-2">How to get the channel page (HTML only)</strong>
            <ol className="m-0 pl-3 list-decimal">
              <li>Open the YouTube channel in your browser (e.g. <code>youtube.com/@channelname</code> or <code>youtube.com/channel/UC...</code>).</li>
              <li>Open the <strong>Videos</strong> tab and scroll to the bottom so the <strong>entire</strong> grid of videos is visible.</li>
              <li>Save the page: <kbd>Ctrl+S</kbd> (Windows/Linux) or <kbd>Cmd+S</kbd> (Mac), then choose <strong>“Web Page, HTML only”</strong> (not “Complete”).</li>
              <li>Or right‑click the page → <strong>Save as</strong> → <strong>“Web Page, HTML only”</strong>.</li>
              <li>Drop the saved <code>.html</code> file below.</li>
            </ol>
          </div>
          <div
            {...getHtmlRootProps()}
            className="p-4 border-2 border-dashed surface-border border-round cursor-pointer"
          >
            <input {...getHtmlInputProps()} />
            <p className="m-0"><strong>Drop saved channel/videos page HTML here</strong></p>
          </div>
        </div>
        <div className="flex flex-wrap align-items-center gap-3">
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="runGenAI"
              checked={runGenAIClassification}
              onChange={(e) => setRunGenAIClassification(e.checked ?? true)}
            />
            <label htmlFor="runGenAI">Run AI classification</label>
          </div>
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="compactView"
              checked={compactView}
              onChange={(e) => setCompactView(e.checked ?? false)}
            />
            <label htmlFor="compactView">Compact table (hide title column)</label>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap align-items-center">
          {channelId.trim() && videos.length > 0 && (
            <Button
              label="Fetch channel videos"
              icon="pi pi-refresh"
              loading={loadingFetchChannel}
              disabled={loadingFetchChannel || loadingClassify || loadingParse}
              onClick={fetchChannelVideos}
              severity="secondary"
              tooltip="Load extra videos from this channel (e.g. private or Shorts) that are not on the saved page. Uses the channel detected from the HTML."
              tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
            />
          )}
          <Button
            label="Classify"
            icon="pi pi-play"
            loading={loadingClassify}
            disabled={
              loadingClassify ||
              loadingParse ||
              (videos.length === 0 && extraVideos.length === 0) ||
              !runGenAIClassification
            }
            onClick={runClassify}
            tooltip="Run AI classification on all loaded videos and save the run. Detected channel ID from the HTML is sent so results can be linked to the channel."
            tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
          />
          {videos.length > 0 && (
            <Button
              label="Download parsed list"
              icon="pi pi-download"
              disabled={loadingParse}
              onClick={exportParsedList}
              severity="secondary"
              tooltip="Download the list of video IDs from the parsed HTML as CSV (before classification)."
              tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
            />
          )}
          {runId && (
            <>
              <Button
                label="Download results (full CSV)"
                icon="pi pi-download"
                onClick={() => {
                  trackToolAction("Classifiers", "download");
                  musicClassifierApi.downloadExport(runId);
                }}
                severity="secondary"
                tooltip="Download classification results as CSV with all columns including description."
                tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
              />
              <Button
                label="Download results (compact CSV)"
                icon="pi pi-download"
                onClick={() => {
                  trackToolAction("Classifiers", "download");
                  musicClassifierApi.downloadExport(runId, { compact: true });
                }}
                severity="secondary"
                outlined
                tooltip="Download classification results as CSV without the description column."
                tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
              />
            </>
          )}
          <Button
            label="Run history"
            icon="pi pi-history"
            loading={loadingRunHistory}
            disabled={loadingRunHistory}
            onClick={loadRunHistory}
            severity="help"
            tooltip="View recent classification runs and download their CSV exports."
            tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
          />
        </div>
      </div>
      {videos.length > 0 && (
        <div className="surface-100 border-round p-3 mb-3">
          {videos.some((v) => typeof v.music_note === "boolean") ? (
            <>
              <strong>{videos.length} videos detected</strong>
              {" · "}
              <strong>{videos.filter((v) => v.music_note === true).length} with music note</strong>
            </>
          ) : (
            <strong>{videos.length} videos loaded</strong>
          )}
        </div>
      )}
      {channelSummary && (
        <div className="surface-100 border-round p-3 mb-3">
          <strong>{channelSummary.fromHtml} from page</strong>
          {" · "}
          <strong>{channelSummary.extraFromChannel} extra from channel</strong>
          {channelSummary.extraFromChannel > 0 && (
            <span className="text-color-secondary ml-2">
              (not on saved page, e.g. private or Shorts)
            </span>
          )}
          {channelInMusicCms !== null && (
            <>
              {" · "}
              <strong>Channel in Music CMS: {channelInMusicCms ? "Yes" : "No"}</strong>
            </>
          )}
          {channelDetails && (
            <div className="mt-2">
              <strong>Channel:</strong> {channelDetails.title}
              {" · "}
              {channelDetails.subscriberCount.toLocaleString()} subscribers
              {channelDetails.defaultLanguage != null && (
                <> · Default language: {channelDetails.defaultLanguage}</>
              )}
              {channelDetails.madeForKids !== undefined && (
                <> · Made for kids: {channelDetails.madeForKids ? "Yes" : "No"}</>
              )}
              {channelDetails.contentOwner != null && (
                <> · Content owner: {channelDetails.contentOwner}</>
              )}
            </div>
          )}
          {results.length === 0 && (
            <p className="m-0 mt-2 text-color-secondary">
              Click <strong>Classify</strong> to run classification on all {channelSummary.fromHtml + channelSummary.extraFromChannel} videos.
            </p>
          )}
        </div>
      )}
      {results.length > 0 && truncatedRunInfo && (
        <div className="surface-100 border-round p-3 mb-3">
          <span className="text-color-secondary">
            Showing first {MAX_VIDEOS_PER_RUN} of {truncatedRunInfo.totalResultCount} results.
          </span>
        </div>
      )}
      {results.length > 0 && (() => {
        const musicCount = results.filter((r) => r.is_musical_opportunity === true).length;
        const totalViews = results.reduce((sum, r) => sum + (r.views ?? 0), 0);
        const musicViews = results.reduce(
          (sum, r) => sum + (r.is_musical_opportunity === true ? (r.views ?? 0) : 0),
          0
        );
        const pctVideos = results.length > 0 ? Math.round((musicCount / results.length) * 100) : 0;
        const pctViews = totalViews > 0 ? Math.round((musicViews / totalViews) * 100) : null;
        return (
          <div className="surface-100 border-round p-3 mb-3">
            <strong>{pctVideos}% of videos</strong> are music (AI)
            {pctViews != null ? (
              <> · <strong>{pctViews}% of views</strong> from music</>
            ) : (
              <> · <span className="text-color-secondary">% of views: —</span> (no view data)</>
            )}
          </div>
        );
      })()}
      {results.length > 0 && (
        <DataTable value={results} paginator rows={10} className="mt-4">
          <Column field="videoId" header="Video ID" />
          {!compactView && <Column field="title" header="Title" />}
          <Column
            field="views"
            header="Views"
            body={(row) =>
              row.views != null ? row.views.toLocaleString() : "—"
            }
          />
          <Column
            field="music_note"
            header="Music note (from page)"
            body={(row) =>
              row.music_note === true
                ? "Yes"
                : row.music_note === false
                  ? "No"
                  : row.music_note === "excluded"
                    ? "Excluded"
                    : "—"
            }
          />
          <Column field="category" header="AI result" />
          <Column
            field="confidence"
            header="AI confidence"
            body={(row) => `${Math.round((row.confidence ?? 0) * 100)}%`}
          />
          <Column
            field="error"
            header="Error"
            body={(row) => row.error ?? "—"}
          />
        </DataTable>
      )}
      {runs.length > 0 && (
        <div className="mt-4">
          <h4>Recent runs</h4>
          <ul>
            {runs.map((r) => (
              <li key={r.runId}>
                {r.dateCreated} {r.channelId && `(${r.channelId})`}{" "}
                <Button
                  label="Download CSV"
                  link
                  size="small"
                  onClick={() => {
                    trackToolAction("Classifiers", "download");
                    musicClassifierApi.downloadExport(r.runId);
                  }}
                />
                {" "}
                <Button
                  label="Compact"
                  link
                  size="small"
                  onClick={() => {
                    trackToolAction("Classifiers", "download");
                    musicClassifierApi.downloadExport(r.runId, { compact: true });
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MusicClassifierTab;
